/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port W3 (Wave-0 infra) — buildRatingEnvelope (§32).
 *
 * A PURE module: given (part, mfgId, input) it returns the §32 POST /rating/
 * envelope with the verbatim MFG-id→manufacturer maps + the per-mfg
 * function_name / type / key-stripping quirks. Centralised so W4-W6 import the
 * SAME maps (the inventory §32 says "copy verbatim").
 *
 * The frontend MFG-id → manufacturer maps (§32, copied verbatim):
 *   fan:     {47 greenheck, 64 twin_city_fan, 152 basx, 164 rosenberg,
 *             37 ebm_papst, 23 comefri, 67 ziehl_abegg, 221 lau, 209 nyblower}
 *   erwheel: {60 innergy_tech, 95 semco, 52 heatex, 233 airotor, 237 annexair}
 *   phx:     {167 recuperator, 52 heatex}
 *   coil:    derives manufacturer = mfg.name.toLowerCase().replace(' ','_')
 *            from the mfgs collection (mfg_id 21 is hard-stubbed to an error
 *            without calling the API).
 *
 * Per-mfg FAN quirks (§32/§33.1 — listed here for the fan wave to consume):
 *   - rosenberg:    function_name 'search_fans'
 *   - greenheck:    function_name 'get_fan_data' + type 'plug'
 *   - ebm_papst:    function_name 'SEARCH_PRODUCTS' + type 'plug'
 *   - ziehl_abegg:  function_name 'SEARCH_PRODUCTS' + type 'plug'
 *   - comefri:      function_name 'fan_rate'
 *   - others:       bare (no function_name, no forced type)
 *   - isolator/mtr keys stripped for the TCF-family except mtr_controller_id
 *     (the fan selector strips those before calling this; not done here — this
 *     module only stamps function_name/type/manufacturer).
 *   - construction_class_id appended from the model when present.
 *
 * erwheel: NO `type`; manufacturer from the erwheel map; envelope is
 *   {part:'erwheel', manufacturer, input}.
 * phx: NO `type`; manufacturer from the phx map.
 * coil: manufacturer derived from the mfgs collection name (passed in).
 */

import type { RatingEnvelope } from './ratingApi';

/** §32 FAN MFG-id → manufacturer (copy verbatim). Dev-sync H: 237 annexair
 * (fan.selector.js RATING_AVAILABLE :177 + MFG_MAPPING :4066 on merged dev —
 * route `rating.fan.annexair`; generic envelope branch: no function_name, no
 * forced type, so buildRatingEnvelope needs no per-mfg quirk entry). */
export const FAN_MFG_MAPPING: Record<number, string> = {
  47: 'greenheck',
  64: 'twin_city_fan',
  152: 'basx',
  164: 'rosenberg',
  37: 'ebm_papst',
  23: 'comefri',
  67: 'ziehl_abegg',
  221: 'lau',
  209: 'nyblower',
  237: 'annexair',
};

/** §32 ERWHEEL MFG-id → manufacturer (copy verbatim — energy_wheel.selector.js:2532). */
export const ERWHEEL_MFG_MAPPING: Record<number, string> = {
  60: 'innergy_tech',
  95: 'semco',
  52: 'heatex',
  233: 'airotor',
  237: 'annexair',
};

/** §32 PHX MFG-id → manufacturer (copy verbatim). */
export const PHX_MFG_MAPPING: Record<number, string> = {
  167: 'recuperator',
  52: 'heatex',
};

/** Per-mfg FAN function_name (§33.1). Absent ⇒ no function_name. */
export const FAN_FUNCTION_NAME: Record<string, string> = {
  rosenberg: 'search_fans',
  greenheck: 'get_fan_data',
  ebm_papst: 'SEARCH_PRODUCTS',
  ziehl_abegg: 'SEARCH_PRODUCTS',
  comefri: 'fan_rate',
};

/** FAN manufacturers that force input type 'plug' (§33.1). */
export const FAN_PLUG_TYPE_MFGS = new Set<string>([
  'greenheck',
  'ebm_papst',
  'ziehl_abegg',
]);

/** Resolve a part's MFG-id → manufacturer string. coil derives its own name and
 * passes it via `manufacturerOverride`. Returns undefined for an unmapped id. */
export function manufacturerFor(
  part: string,
  mfgId: number | string | null | undefined
): string | undefined {
  const id = Number(mfgId);
  if (part === 'erwheel') return ERWHEEL_MFG_MAPPING[id];
  if (part === 'fan') return FAN_MFG_MAPPING[id];
  if (part === 'plate_heat_exchanger') return PHX_MFG_MAPPING[id];
  return undefined;
}

export interface BuildEnvelopeOptions {
  /** coil passes the derived manufacturer name directly (mfg.name.toLowerCase). */
  manufacturerOverride?: string;
  /** appended verbatim when present (fan §33.1). */
  constructionClassId?: number | null;
}

/**
 * Build the §32 envelope for (part, mfgId, input).
 *   - erwheel / phx: NO `type` (the input may carry per-season fields stripped
 *     upstream by the selector); manufacturer from the map.
 *   - fan: function_name + forced 'plug' type per the per-mfg quirk maps;
 *     construction_class_id appended when given.
 *   - coil: manufacturer from manufacturerOverride.
 * The caller (the selector) owns input assembly + per-season/WB-RH field
 * stripping; this is the centralised manufacturer/function_name/type stamp.
 */
export function buildRatingEnvelope(
  part: string,
  mfgId: number | string | null | undefined,
  input: Record<string, unknown>,
  opts: BuildEnvelopeOptions = {}
): RatingEnvelope {
  const manufacturer =
    opts.manufacturerOverride ?? manufacturerFor(part, mfgId) ?? '';
  const envelope: RatingEnvelope = { part, manufacturer, input };

  if (part === 'fan') {
    const fn = FAN_FUNCTION_NAME[manufacturer];
    if (fn) envelope.function_name = fn;
    if (FAN_PLUG_TYPE_MFGS.has(manufacturer)) envelope.type = 'plug';
    if (opts.constructionClassId != null)
      envelope.construction_class_id = opts.constructionClassId;
  }
  // erwheel + phx: no `type`, no function_name (the §33.3/§33.4 envelopes are
  // bare {part, manufacturer, input}).
  return envelope;
}
